import { CommonModule } from '@angular/common';
import { Component, inject, Input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, map, Observable } from 'rxjs';
import { LIB_CONFIG } from '../../../lib.config';
import { Ort } from '../../../model/ort';

import { RegionenService } from '../../../services/facetten/instance/regionen.service';
import { FilterAggregationsService } from '../../../services/filter-aggregations.service';
import { AktiveFilterService } from '../../../services/filter/aktive-filter.service';
import { ErweiterteSucheService } from '../../../services/filter/erweiterte-suche.service';
import { LoaderService } from '../../../services/loader.service';
import { UrlService } from '../../../services/state/url.service';
import { BerufeService } from '../../../services/suchseite/berufe.service';
import { CorrectionService } from '../../../services/suchseite/correction.service';
import { OrtService } from '../../../services/suchseite/ort.service';
import { PaginationService } from '../../../services/suchseite/pagination.service';
import { RelaxationService } from '../../../services/suchseite/relaxation.service';
import { SuchwortService } from '../../../services/suchseite/suchwort.service';
import {
  FavoriteToggleComponent,
  Weiterbildungssuche
} from '../../detail/favorite-toggle/favorite-toggle.component';

@Component({
  selector: 'now-wbsuche-lib-suchergebnis-hits-card',
  templateUrl: 'suchergebnis-hits-card.component.html',
  styleUrls: ['./suchergebnis-hits-card.component.scss'],
  imports: [CommonModule, FavoriteToggleComponent]
})
export class SuchergebnisHitsCardComponent {
  private readonly suchwortService = inject(SuchwortService);
  private readonly orteService = inject(OrtService);
  private readonly correctionService = inject(CorrectionService);
  private readonly relaxationService = inject(RelaxationService);
  private readonly aggregationService = inject(FilterAggregationsService);
  private readonly paginationService = inject(PaginationService);
  private readonly loaderService = inject(LoaderService);
  private readonly aktiveFilterService = inject(AktiveFilterService);
  private readonly route = inject(ActivatedRoute);
  private readonly berufeService = inject(BerufeService);
  private readonly regionenService = inject(RegionenService);

  @Input() public maximaleTreffer!: number;
  @Input() public hasMoreHits: boolean = false;

  public isNowWbSuche = inject(LIB_CONFIG).isNowWbSuche;

  public showRequiredInputState$ = this.suchwortService.showRequiredInputState$;
  public suchwort$ = this.suchwortService.suchwort$;
  public bezeichnung$ = this.berufeService.bezeichnung$;
  public ort$ = this.orteService.ort$;
  public correction$ = this.correctionService.correction$;
  public removedToken$ = this.relaxationService.removedToken$;
  public hasMoreHits$ = this.aggregationService.count$.pipe(map((count) => count > 10000));
  public pageInfos$ = this.paginationService.pageInfos$;
  public bildungsangeboteLoading$ = this.loaderService.bildungsangeboteLoading$;

  ergebnisZaehlerText$ = combineLatest([
    this.suchwort$,
    this.correction$,
    this.removedToken$,
    this.bezeichnung$
  ]).pipe(
    map(([suchwort, correction, removedToken, bezeichnung]) => {
      let formatedSuchwort;
      if (removedToken) {
        formatedSuchwort = suchwort.replace(removedToken, `<s class="fw-bold">${removedToken}</s>`);
      } else {
        formatedSuchwort = correction || suchwort || bezeichnung;
      }
      return `für <span class="fw-bold">${formatedSuchwort}</span>`;
    })
  );

  selectedBundesland$ = this.regionenService.getSelectItems$().pipe(
    map((alleBundeslaender) => {
      const selectedBundeslandLabel = alleBundeslaender.find(({ selected }) => selected)?.label;
      return selectedBundeslandLabel ?? '';
    })
  );

  public triggerSearchWithoutCorrection() {
    this.correctionService.disableCorrection();
  }

  get favoriteEntity$(): Observable<Weiterbildungssuche> {
    return combineLatest([
      this.aktiveFilterService.aktiverUrlFilter$.pipe(
        map((filter) => filter.map(({ bezeichnung }) => bezeichnung))
      ),
      this.suchwort$,
      this.ort$,
      this.route.queryParams.pipe(
        map((paramMap) =>
          Object.entries(paramMap)
            .filter(
              ([k, v]) =>
                ![
                  UrlService.AKTUELLE_SEITE,
                  UrlService.SELECTED_ANGEBOT,
                  ErweiterteSucheService.URL_PARAM
                ].includes(k)
            )
            .map(([k, v]) => `${k}=${encodeURI(v)}`)
            .sort((a, b) => a.localeCompare(b, 'de'))
            .join('&')
        )
      )
    ]).pipe(
      map(([aktiveFilter, suchwort, ort, urlParams]: [string[], string, Ort | null, string]) => ({
        basisdienst: 'WEITERBILDUNGSSUCHE',
        detailsSearchFilter: JSON.stringify(aktiveFilter),
        detailsSearchKeywords: suchwort,
        detailsSearchLocation: ort?.name,
        detailsSearchPostcode: ort?.postleitzahl,
        favoritTitle: suchwort ?? 'ohne Suchbegriff',
        targetId: urlParams
      }))
    );
  }
}
